// Full "Enabled workspaces" panel with an External / Internal / All
// filter chip. Sits on the FeatureDetail page so SEs can slice the
// enabled list without leaving the launch. Cards elsewhere just show
// the top ~3 via <EnabledWorkspacesLine>.
import { useMemo, useState } from "react";
import type { FeatureFlag, WorkspaceInfo } from "@release-hub/shared";
import { useWorkspaces } from "@/api/queries";
import {
  effectiveWorkspaces,
  groupWorkspacesByClassification,
} from "@/lib/featureWorkspaces";

type FilterMode = "external" | "internal" | "all";

const FILTERS: { value: FilterMode; label: string }[] = [
  { value: "external", label: "External" },
  { value: "internal", label: "Internal" },
  { value: "all", label: "All" },
];

export function EnabledWorkspacesPanel({ flags }: { flags: FeatureFlag[] }) {
  const { data: workspaces } = useWorkspaces();
  const [mode, setMode] = useState<FilterMode>("external");

  const view = useMemo(() => {
    if (!workspaces) return null;
    const eff = effectiveWorkspaces(flags, workspaces);
    if (eff.isEveryone) return { kind: "everyone" as const };
    if (eff.ids.length === 0) return null;
    const wsById = new Map(workspaces.map((w) => [w.id, w]));
    const enabled = eff.ids
      .map((id) => wsById.get(id))
      .filter((w): w is WorkspaceInfo => !!w);
    return {
      kind: "some" as const,
      total: enabled.length,
      grouped: groupWorkspacesByClassification(enabled),
    };
  }, [flags, workspaces]);

  if (!view) return null;

  if (view.kind === "everyone") {
    return (
      <section>
        <h3 className="mb-2 font-serif text-lg">Enabled workspaces</h3>
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-text-secondary">
          Rolled out to all workspaces.
        </div>
      </section>
    );
  }

  const { grouped } = view;
  // "External" is `revenue` + `unclassified` — the latter is a fallback so
  // customers that never got a classification tag still show up for SEs
  // instead of hiding behind the "internal" filter.
  const rowsForMode: Record<FilterMode, WorkspaceInfo[]> = {
    external: [
      ...grouped.external,
      ...grouped.unclassified,
      ...grouped.partner,
      ...grouped.selfServe,
    ],
    internal: grouped.internal,
    all: [
      ...grouped.external,
      ...grouped.unclassified,
      ...grouped.partner,
      ...grouped.selfServe,
      ...grouped.internal,
    ],
  };

  const counts: Record<FilterMode, number> = {
    external: rowsForMode.external.length,
    internal: rowsForMode.internal.length,
    all: rowsForMode.all.length,
  };

  const active = rowsForMode[mode].slice().sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <section>
      <h3 className="mb-2 font-serif text-lg">Enabled workspaces</h3>
      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setMode(f.value)}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                mode === f.value
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-border bg-transparent text-text-secondary hover:border-border-hover hover:text-text"
              }`}
            >
              {f.label}
              <span className="ml-1 text-text-tertiary">{counts[f.value]}</span>
            </button>
          ))}
          <span className="ml-auto text-xs text-text-tertiary">
            {view.total} total
          </span>
        </div>
        {active.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            No workspaces in this bucket.
          </p>
        ) : (
          <ul className="grid max-h-64 grid-cols-1 gap-x-4 gap-y-0.5 overflow-y-auto text-sm sm:grid-cols-2">
            {active.map((w) => (
              <li
                key={w.id}
                className="truncate rounded px-1.5 py-0.5 hover:bg-border/40"
                title={
                  w.classification
                    ? `${w.name} · ${w.classification}`
                    : `${w.name} · unclassified`
                }
              >
                {w.name}
                {w.classification == null && (
                  <span className="ml-1 text-text-tertiary">·</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
